import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { ApiError } from '@/api/client';
import { useAuth } from '@/features/auth/useAuth';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setFormError(null);

    try {
      await login(values);
      const from = (location.state as { from?: string } | null)?.from;
      void navigate(from ?? '/', { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        // Map field errors back onto the form; anything else is a banner.
        const fieldErrors = error.fieldErrors;
        const keys = Object.keys(fieldErrors);

        if (keys.length > 0) {
          for (const key of keys) {
            setError(key as keyof FormValues, {
              message: fieldErrors[key]?.[0] ?? 'Invalid value',
            });
          }
        } else {
          setFormError(error.detail ?? error.title);
        }
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  };

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="h1">Sign in</Typography>
            <Typography variant="body2" color="text.secondary">
              Quotient AI — quotation platform
            </Typography>
          </Stack>

          {formError && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                autoFocus
                fullWidth
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                {...register('email')}
              />

              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                fullWidth
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
                {...register('password')}
              />

              <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>
          </form>

          <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Forgot password?
            </Link>
            <Link component={RouterLink} to="/register" variant="body2">
              Create an account
            </Link>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
